import React, { useCallback, useMemo, useContext, useEffect, useState } from 'react'
import useGroomingVisit from '../../../../../hooks/useGroomingVisit'
import OptionsMenu from '../../../../Common/OptionsMenu/OptionsMenu'
import SectionFormContainer from '../../../components/SectionFormContainer'
import { FONT_WEIGHTS, capitilizeMultipleWords } from '../../../../../utils'
import GroomingAppointmentWizardContext from '../GroomingAppointmentWizardContext'
import ActivityAlert from '../../../components/ActivityAlert'
import { GROOMING_SERVICE_TYPES } from '../../../../../utils'
import moment from 'moment'
import { makeStyles } from '@material-ui/core'
import { gray } from '../../../../../utils/colors'

const useStyles = makeStyles(() => ({
	alertsContainer: {
		marginTop: '.8rem'
	},
	availabilityText: {
		fontSize: '0.875rem',
		fontFamily: 'Nunito',
		fontWeight: FONT_WEIGHTS.Bold,
		color: gray[900]
	}
}))

const ServiceSelection = (props) => {
	const classes = useStyles()
	const {
		wizardContextProvider: { grooming_appointment, setWizardAttributeAndValues }
	} = useContext(GroomingAppointmentWizardContext)
	const { visit_attr, title = '', subtext = '' } = props
	const { loading, facility, getGeneralAvailability, fetchingAvailability } = useGroomingVisit()
	const [generalAvailability, setGeneralAvailability] = useState({
		[GROOMING_SERVICE_TYPES.cleanup]: null,
		[GROOMING_SERVICE_TYPES.haircut]: null,
		[GROOMING_SERVICE_TYPES.trim]: null,
		[GROOMING_SERVICE_TYPES.bath]: null
	})

	useEffect(() => {
		const fetchGeneralAvailability = async () => {
			const availability = await getGeneralAvailability(grooming_appointment)
			setGeneralAvailability((prev) => {
				return { ...prev, [grooming_appointment[visit_attr]]: availability }
			})
		}
		if (
			getGeneralAvailability &&
			grooming_appointment[visit_attr] > '' &&
			!generalAvailability[grooming_appointment[visit_attr]]
		) {
			fetchGeneralAvailability()
		}
	}, [getGeneralAvailability, grooming_appointment])

	const soonestAvailability = useMemo(() => {
		if (grooming_appointment[visit_attr] === '') {
			return ''
		}

		const availability = generalAvailability[grooming_appointment[visit_attr]]
		const { shifts } = availability || { shifts: [] }

		if (shifts.length > 0) {
			const [firstShift] = shifts
			const { date } = firstShift
			const formatDate = moment(date).format('dddd, MMMM Do, YYYY')
			return `Soonest available date: ${formatDate}`
		} else {
			return ''
		}
	}, [generalAvailability, grooming_appointment])

	const handleSelection = useCallback(
		(type) => {
			const newValues = { ...grooming_appointment }
			newValues[visit_attr] = type

			// trim and cleanup also need to affect trim amount so need to add some
			// non database related code here
			// NOTE: if other grooming_services are ever added to the db for a facility,
			// then this code will need to be updated to reflect those associated values
			// if needed.
			if (type === GROOMING_SERVICE_TYPES.trim) {
				newValues['trim_amount'] = 'Feet, Face, Sanitary'
			}
			if (type === GROOMING_SERVICE_TYPES.cleanup) {
				newValues['trim_amount'] = 'No Trim'
			}
			// haircut trim_amount is recorded in the BodyStyle step.
			if (type === GROOMING_SERVICE_TYPES.haircut) {
				newValues['trim_amount'] = ''
			}

			setWizardAttributeAndValues(newValues)
		},
		[grooming_appointment, setWizardAttributeAndValues, visit_attr]
	)

	const options = useMemo(() => {
		if (
			!facility ||
			!facility.services ||
			!facility.services.grooming ||
			!facility.services.grooming.services
		) {
			return []
		}

		const {
			services: {
				grooming: { services: grooming_services }
			}
		} = facility || []

		const opts = []

		grooming_services.forEach(({ name, order, type, available, description }) => {

			const option = {
				title: capitilizeMultipleWords(name, ' '),
				description,
				id: order,
				disabled:
					loading || !available || grooming_appointment[visit_attr] === type || fetchingAvailability,
				selected: grooming_appointment[visit_attr] === type,
				onSelected: () => handleSelection(type)
			}

			opts[order - 1] = option

		})
		return opts
	}, [facility, handleSelection, loading, grooming_appointment, visit_attr, fetchingAvailability])

	return (
		<SectionFormContainer title={title} subtext={subtext}>
			<OptionsMenu options={options} />
			<div className={classes.alertsContainer}>
				{fetchingAvailability ? (
					<ActivityAlert text="Fetching soonest availability for that service" />
				) : null}
				{soonestAvailability > '' ? (
					<div className={classes.availabilityText}>{soonestAvailability}</div>
				) : null}
			</div>
		</SectionFormContainer>
	)
}

export default ServiceSelection
